import React from 'react';
import styles from '../search.page.module.scss';
import { Button } from 'src/components/design-system';
import { SEARCH_TYPE } from 'shared/constants';
import { useShowMoreResults } from './use-show-more-results';
import { SearchV2ExtendedResults as ExtendedResultsJs } from './search-v2-extended-results.component';

interface SearchHit {
  type: string;
  item: Record<string, unknown>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- renderers are untyped JS
type RenderRow = (props: { item: any }, index: number, options: any) => React.ReactNode;

interface SearchV2BucketResultsProps {
  results: SearchHit[];
  extended: SearchHit[];
  renderByType: Record<string, RenderRow | undefined>;
  options: Record<string, unknown>;
  section: string;
  sectionType: string | ((entry: SearchHit) => string);
  sectionPosition: number;
  sectionCollapsed: boolean;
  resetKey: unknown;
}

// The toggle is untyped JS; its `entries = []` default would otherwise infer `never[]`.
const SearchV2ExtendedResults = ExtendedResultsJs as unknown as React.ComponentType<
  Record<string, unknown>
>;

/**
 * One search-v2 group's rows: the first `SEARCH_V2_GROUP_PAGING.INITIAL` exact hits, a
 * "Show more" link that reveals `STEP` more per click until none are hidden, then the
 * related-resources toggle (`SearchV2ExtendedResults`, paged the same way once open),
 * which only appears once every exact hit is on screen so the two controls never stack.
 * A new search (`resetKey`) starts the group over.
 *
 * @param props - The group's `results` and `extended` tiers, the row renderers keyed by
 *   API type, the analytics `options`, the section's name/type/position/collapsed state
 *   (`sectionType` may be a function of the entry for the mixed single-workspace list)
 *   and the `resetKey` that changes with the search.
 * @returns The rows and controls, as fragments for the enclosing list.
 * @example
 * <SearchV2BucketResults
 *   results={bucket.results}
 *   extended={bucket.extended}
 *   renderByType={renderByType}
 *   options={options}
 *   section={name}
 *   sectionType="workspace"
 *   sectionPosition={2}
 *   sectionCollapsed={!isOpen}
 *   resetKey={results}
 * />
 */
const SearchV2BucketResults = ({
  results,
  extended,
  renderByType,
  options,
  section,
  sectionType,
  sectionPosition,
  sectionCollapsed,
  resetKey,
}: SearchV2BucketResultsProps) => {
  const fixedSectionType = typeof sectionType === 'function' ? 'workspace' : sectionType;
  const { visibleCount, remaining, label, onShowMore, liveRegion } = useShowMoreResults({
    total: results.length,
    resetKey,
    searchType: SEARCH_TYPE.REGULAR,
    section,
    sectionType: fixedSectionType,
    sectionPosition,
  });

  const sectionTypeOf = (entry: SearchHit) =>
    typeof sectionType === 'function' ? sectionType(entry) : sectionType;

  return (
    <>
      {results.slice(0, visibleCount).map((entry, index) => {
        const render = renderByType[entry.type];
        if (!render) return null;
        return render({ item: entry.item }, index, {
          ...options,
          section,
          sectionType: sectionTypeOf(entry),
          sectionPosition,
          sectionCollapsed,
          position: index + 1,
        });
      })}

      {remaining > 0 && (
        <div className={styles.extendedResults}>
          <Button type="link" onClick={onShowMore}>
            {label}
          </Button>
        </div>
      )}
      {liveRegion}

      {remaining === 0 && (
        <SearchV2ExtendedResults
          entries={extended}
          renderByType={renderByType}
          hasLiteralResults={results.length > 0}
          resetKey={resetKey}
          sectionContext={{
            ...options,
            section,
            sectionType: fixedSectionType,
            sectionPosition,
            sectionCollapsed,
            positionOffset: results.length,
            literalResultsCount: results.length,
          }}
        />
      )}
    </>
  );
};

export { SearchV2BucketResults };
